import { useState, useCallback, memo, useEffect } from 'react';
import moment from 'moment';
import Filters from '@pushpay/filters';
import * as LucideIcons from 'lucide-react';
import { PageNavigation, PrimaryAction, KebabMenuItem } from './PageNavigation';
import { PageBackground } from './PageBackground';
import { SearchBar } from './SearchBar';
import { Table, TableColumn } from './table';
import { EntityGallery } from './EntityGallery';

// View mode type
export type ViewMode = 'table' | 'gallery';

// Hook to detect window size
const useWindowSize = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Entity Table Component
interface EntityTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  getRowKey: (item: T) => string | number;
  emptyMessage?: string;
  pageSize?: number;
  onRowClick?: (item: T, index: number) => void;
  loadingMore?: boolean;
}

function EntityTableComponent<T>({ 
  data, columns, getRowKey, 
  emptyMessage = 'No items found',
  pageSize = 10, onRowClick, loadingMore = false
}: EntityTableProps<T>) {
  return (
    <Table<T>
      data={data} columns={columns} getRowKey={getRowKey}
      usePagination={true} pageSize={pageSize}
      emptyMessage={emptyMessage} onRowClick={onRowClick}
      loadingMore={loadingMore}
    />
  );
}

const EntityTable = memo(EntityTableComponent) as typeof EntityTableComponent;

// Main EntityList Component
export interface EntityListProps<T> {
  // Page configuration
  objectTitle: string; // e.g., "Groups", "People", "Transactions"
  
  // Primary action configuration
  primaryAction?: PrimaryAction | null;
  
  // Additional kebab menu items (Download CSV and Print are included by default)
  kebabMenuItems?: KebabMenuItem[];
  
  // Search configuration
  searchPlaceholder?: string;
  onSearchChange?: (query: string) => void;
  
  // View configuration
  defaultView?: ViewMode;
  enableGalleryView?: boolean;
  
  // Table configuration
  columns: TableColumn<T>[];
  getRowKey: (item: T) => string | number;
  emptyMessage?: string;
  pageSize?: number;
  onRowClick?: (item: T, index: number) => void;
  
  // Gallery configuration (required if enableGalleryView is true)
  renderGalleryTile?: (item: T, index: number) => React.ReactNode;
  galleryPageSize?: number;
  
  // Data
  filteredData: T[];
  
  // Loading state
  loadingMore?: boolean; // Shows subtle loading indicator when fetching additional data
  
  // Filters configuration (optional)
  filterDefinitions?: any[];
  onApplyFilters?: (filters: any) => void;
  
  // Handlers for default menu items
  onDownloadCSV?: () => void;
  onPrint?: () => void;
}

function EntityListComponent<T>({
  objectTitle,
  primaryAction,
  kebabMenuItems = [],
  searchPlaceholder,
  onSearchChange,
  defaultView = 'table',
  enableGalleryView = false,
  columns,
  getRowKey,
  emptyMessage,
  pageSize = 10,
  onRowClick,
  renderGalleryTile,
  galleryPageSize = 12,
  filteredData,
  loadingMore = false,
  filterDefinitions = [],
  onApplyFilters,
  onDownloadCSV,
  onPrint,
}: EntityListProps<T>) {
  const isMobile = useWindowSize();
  const [showFilters, setShowFilters] = useState(false);
  
  // Storage key for view mode preference
  const storageKey = `entityList-${objectTitle.toLowerCase().replace(/\s+/g, '-')}-viewMode`;
  
  // Initialize view mode from sessionStorage or use default
  const getInitialViewMode = (): ViewMode => {
    if (!enableGalleryView) return 'table';
    
    // Check sessionStorage first
    const savedView = sessionStorage.getItem(storageKey) as ViewMode | null;
    if (savedView === 'table' || savedView === 'gallery') {
      return savedView;
    }
    
    // Check if mobile on initial load (window.innerWidth is available immediately)
    const isInitialMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    
    // Default to gallery on mobile, otherwise use provided default
    return isInitialMobile ? 'gallery' : defaultView;
  };
  
  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
  
  // Update view mode if screen size changes and no user preference is saved
  useEffect(() => {
    const savedView = sessionStorage.getItem(storageKey);
    if (!savedView && enableGalleryView) {
      // Only update if user hasn't explicitly set a preference
      const newMode = isMobile ? 'gallery' : defaultView;
      if (newMode !== viewMode) {
        setViewMode(newMode);
      }
    }
  }, [isMobile, enableGalleryView, defaultView, storageKey, viewMode]);
  
  // Save view mode to sessionStorage whenever it changes
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    sessionStorage.setItem(storageKey, mode);
  }, [storageKey]);

  // Combine default kebab menu items with custom ones
  const allKebabMenuItems: KebabMenuItem[] = [
    // Add mobile-only primary action if it exists
    ...(primaryAction ? [{
      ...primaryAction,
      mobileOnly: true
    }] : []),
    // Custom menu items
    ...kebabMenuItems,
    // Default actions
    ...(onDownloadCSV ? [{
      label: 'Download CSV',
      icon: LucideIcons.Download,
      onClick: onDownloadCSV
    }] : []),
    ...(onPrint ? [{
      label: 'Print',
      icon: LucideIcons.Printer,
      onClick: onPrint
    }] : []),
  ];

  const handleSearchChange = useCallback((query: string) => {
    onSearchChange?.(query);
  }, [onSearchChange]);

  const handleApplyFilters = useCallback((filters: any) => {
    onApplyFilters?.(filters || {});
  }, [onApplyFilters]);

  return (
    <Filters.FiltersProvider
      translate={Filters.defaultEnglishTranslate}
      translateMoment={(value) => value.format()}
      formatCurrency={(value) => `$${value}`}
      createDate={(dateInput) => moment(dateInput)}
    >
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          {/* Header with Title and Actions */}
          <header className="flex justify-between items-center mb-4 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 m-0">{objectTitle}</h1>
            <PageNavigation
              primaryAction={primaryAction}
              kebabMenuItems={allKebabMenuItems}
            />
          </header>

            {/* Search Bar */}
          {onSearchChange && (
            <div className="mb-4 flex gap-2">
              <div className="flex-1">
                <SearchBar
                  onSearchChange={handleSearchChange}
                  placeholder={searchPlaceholder || `Search ${objectTitle.toLowerCase()}...`}
                  width="100%"
                  dataTarget={`${objectTitle.toLowerCase().replace(/\s+/g, '-')}-search-input`}
                />
              </div>
              
              {/* View Mode Toggle */}
              {enableGalleryView && (
                <div className="flex items-center border border-gray-300 rounded-sm overflow-hidden bg-white">
                  <button
                    onClick={() => handleViewModeChange('table')}
                    className={`flex items-center justify-center h-[34px] w-[34px] transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label="Table view"
                    title="Table view"
                  >
                    <LucideIcons.List size={18} />
                  </button>
                  <div className="w-px h-[34px] bg-gray-300" />
                  <button
                    onClick={() => handleViewModeChange('gallery')}
                    className={`flex items-center justify-center h-[34px] w-[34px] transition-colors ${
                      viewMode === 'gallery'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label="Gallery view"
                    title="Gallery view"
                  >
                    <LucideIcons.LayoutGrid size={18} />
                  </button>
                </div>
              )}
              
              {filterDefinitions.length > 0 && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center justify-center h-[34px] w-[34px] rounded-sm border transition-colors ${
                    showFilters 
                      ? 'bg-blue-50 border-blue-500 text-blue-600' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  aria-label="Toggle filters"
                >
                  <LucideIcons.ListFilter size={18} />
                </button>
              )}
            </div>
          )}

          {/* Filters */}
          {filterDefinitions.length > 0 && showFilters && (
            <div className="mb-6">
              <Filters.Bar
                filters={filterDefinitions}
                onApplyFilters={handleApplyFilters}
                isMobileLayout={isMobile}
                applyButtonLabel="Apply"
                clearAllButtonLabel="Clear All"
              />
            </div>
          )}

          {/* Data View - Table or Gallery */}
          {viewMode === 'table' ? (
            <EntityTable
              data={filteredData}
              columns={columns}
              getRowKey={getRowKey}
              emptyMessage={emptyMessage}
              pageSize={pageSize}
              onRowClick={onRowClick}
              loadingMore={loadingMore}
            />
          ) : (
            <EntityGallery
              data={filteredData}
              getRowKey={getRowKey}
              renderTile={renderGalleryTile!}
              emptyMessage={emptyMessage}
              pageSize={galleryPageSize}
              onTileClick={onRowClick}
              loadingMore={loadingMore}
            />
          )}
        </main>
      </PageBackground>

      {/* Portal containers for mobile filter modals */}
      {filterDefinitions.map((filter: any) => (
        <div
          key={`${filter.field}-filter-type-container`}
          data-pp-at-target={`${filter.field}-filter-type-container`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        />
      ))}
    </Filters.FiltersProvider>
  );
}

export const EntityList = memo(EntityListComponent) as typeof EntityListComponent;
